import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Pencil, XCircle, Copy, RefreshCw, Search } from "lucide-react";

/* v40 — Transaction Corrections
   Unified financial-transactions ledger under Reports & P&L.
   Every row is a payment (Residence / Mess / Hostel Guest / Mess Guest).
   Actions:
     • Edit → PUT /api/payments/{id}
     • Void → DELETE /api/payments/{id}?reason=...
   Because both endpoints call _recompute_fee_status internally, Dashboard
   collections, Daily/Monthly reports and P&L auto-refresh.
*/

const MODE_LABELS = {
  cash: "Cash",
  upi: "UPI",
  card: "Card",
  bank_transfer: "Bank Transfer",
  razorpay: "Razorpay",
};

const fmt = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

export default function TransactionCorrectionsTab() {
  const [preset, setPreset] = useState("month");
  const today = new Date().toISOString().slice(0, 10);
  const currMonth = today.slice(0, 7);
  const [dateFrom, setDateFrom] = useState(`${currMonth}-01`);
  const [dateTo, setDateTo] = useState(today);
  const [module, setModule] = useState("all");
  const [status, setStatus] = useState("active");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ items: [], total_active: 0, total_voided: 0,
    count: 0, duplicate_count: 0 });
  const [editing, setEditing] = useState(null);   // payment row
  const [voiding, setVoiding] = useState(null);   // payment row

  function applyPreset(p) {
    setPreset(p);
    const t = new Date();
    if (p === "today") {
      const d = t.toISOString().slice(0, 10);
      setDateFrom(d); setDateTo(d);
    } else if (p === "week") {
      const start = new Date(t); start.setDate(t.getDate() - 6);
      setDateFrom(start.toISOString().slice(0, 10));
      setDateTo(t.toISOString().slice(0, 10));
    } else if (p === "month") {
      setDateFrom(t.toISOString().slice(0, 7) + "-01");
      setDateTo(t.toISOString().slice(0, 10));
    }
  }

  async function load() {
    setLoading(true);
    try {
      const params = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
      if (module !== "all") params.set("module", module);
      if (status !== "all")  params.set("status", status);
      if (q.trim())          params.set("q", q.trim());
      const { data } = await api.get(`/reports/transactions?${params.toString()}`);
      setData(data);
    } catch (e) { toast.error(formatApiError(e) || "Failed to load transactions"); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [dateFrom, dateTo, module, status]);

  const totals = useMemo(() => ({
    count: data.count,
    active: data.total_active,
    voided: data.total_voided,
    dups: data.duplicate_count,
  }), [data]);

  return (
    <div className="space-y-4" data-testid="txn-corrections-tab">
      {/* Filter row */}
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <div className="eyebrow text-muted-foreground mb-1">Period</div>
          <Select value={preset} onValueChange={applyPreset}>
            <SelectTrigger className="w-[140px]" data-testid="txn-preset"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="week">Last 7 days</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
              <SelectItem value="custom">Custom</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <div className="eyebrow text-muted-foreground mb-1">From</div>
          <Input type="date" value={dateFrom} onChange={(e) => { setPreset("custom"); setDateFrom(e.target.value); }}
            className="w-[160px]" data-testid="txn-date-from" />
        </div>
        <div>
          <div className="eyebrow text-muted-foreground mb-1">To</div>
          <Input type="date" value={dateTo} onChange={(e) => { setPreset("custom"); setDateTo(e.target.value); }}
            className="w-[160px]" data-testid="txn-date-to" />
        </div>
        <div>
          <div className="eyebrow text-muted-foreground mb-1">Module</div>
          <Select value={module} onValueChange={setModule}>
            <SelectTrigger className="w-[150px]" data-testid="txn-module"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All modules</SelectItem>
              <SelectItem value="residence">Residence Fees</SelectItem>
              <SelectItem value="mess">Mess Fees</SelectItem>
              <SelectItem value="stay_guest">Hostel Guest</SelectItem>
              <SelectItem value="mess_guest">Mess Guest</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <div className="eyebrow text-muted-foreground mb-1">Status</div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-[140px]" data-testid="txn-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active only</SelectItem>
              <SelectItem value="voided">Voided only</SelectItem>
              <SelectItem value="all">All (incl. voided)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-[200px]">
          <div className="eyebrow text-muted-foreground mb-1">Search</div>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              className="pl-8" placeholder="Name, receipt or reference"
              data-testid="txn-search" />
          </div>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="txn-refresh">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />Refresh
        </Button>
      </div>

      {/* Totals strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile label="Transactions" value={totals.count} />
        <StatTile label="Active Total" value={fmt(totals.active)} tone="accent" />
        <StatTile label="Voided Total" value={fmt(totals.voided)} tone="warn" />
        <StatTile label="Duplicate flags" value={totals.dups} tone={totals.dups ? "warn" : "muted"} />
      </div>

      {/* Table */}
      <Card className="p-0 border-border bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead>
              <tr className="text-left text-muted-foreground eyebrow border-b border-border">
                <th className="py-2 px-3">Date</th>
                <th className="py-2 pr-3">Party</th>
                <th className="py-2 pr-3">Module / Type</th>
                <th className="py-2 pr-3 text-right">Amount</th>
                <th className="py-2 pr-3">Mode</th>
                <th className="py-2 pr-3">Receipt / Ref</th>
                <th className="py-2 pr-3">Recorded by</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2 pr-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={9} className="py-8 text-center text-muted-foreground">Loading…</td></tr>}
              {!loading && (data.items || []).length === 0 && (
                <tr><td colSpan={9} className="py-8 text-center text-muted-foreground">No transactions in this period.</td></tr>
              )}
              {(data.items || []).map((t) => (
                <tr key={t.id} className="border-b border-border/50 hover:bg-secondary/40"
                    data-testid={`txn-row-${t.id}`}>
                  <td className="py-2 px-3 font-mono text-xs whitespace-nowrap">{t.payment_date}</td>
                  <td className="py-2 pr-3">
                    <div className="font-medium truncate max-w-[220px]">{t.party_name || "—"}</div>
                    <div className="text-[11px] text-muted-foreground">
                      {t.party_ref || "—"}
                      {t.room_no ? ` · ${t.room_no}` : ""}
                    </div>
                  </td>
                  <td className="py-2 pr-3 text-xs uppercase tracking-wide text-muted-foreground">
                    <div>{t.module || "—"}</div>
                    <div className="text-[11px]">{t.fee_type || ""}</div>
                  </td>
                  <td className={`py-2 pr-3 text-right font-mono font-semibold ${t.status === "voided" ? "line-through text-muted-foreground" : ""}`}>
                    {fmt(t.amount)}
                  </td>
                  <td className="py-2 pr-3">{MODE_LABELS[t.mode] || t.mode || "—"}</td>
                  <td className="py-2 pr-3">
                    <div className="font-mono text-xs">{t.receipt_no || "—"}</div>
                    <div className="text-[11px] text-muted-foreground truncate max-w-[160px]">{t.reference || ""}</div>
                  </td>
                  <td className="py-2 pr-3 text-xs">{t.received_by_name || "—"}</td>
                  <td className="py-2 pr-3">
                    <div className="flex items-center gap-1 flex-wrap">
                      {t.status === "active" ? (
                        <Badge className="bg-emerald-100 text-emerald-800 border-transparent">Active</Badge>
                      ) : (
                        <Badge className="bg-rose-100 text-rose-800 border-transparent">Voided</Badge>
                      )}
                      {t.is_duplicate && (
                        <Badge className="bg-amber-100 text-amber-800 border-transparent" title="Same amount+mode+date for this party">
                          <Copy className="w-3 h-3 mr-1" />Duplicate
                        </Badge>
                      )}
                    </div>
                  </td>
                  <td className="py-2 pr-3 text-right whitespace-nowrap">
                    {t.status === "active" ? (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => setEditing(t)}
                                data-testid={`txn-edit-${t.id}`}>
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setVoiding(t)}
                                data-testid={`txn-void-${t.id}`}>
                          <XCircle className="w-3.5 h-3.5 text-destructive" />
                        </Button>
                      </>
                    ) : (
                      <span className="text-xs text-muted-foreground italic">
                        {t.reversal_reason || "voided"}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <EditTxnDialog row={editing} onClose={() => setEditing(null)}
                     onDone={() => { setEditing(null); load(); }} />
      <VoidTxnDialog row={voiding} onClose={() => setVoiding(null)}
                     onDone={() => { setVoiding(null); load(); }} />
    </div>
  );
}

function StatTile({ label, value, tone = "" }) {
  const cls = tone === "warn" ? "text-destructive"
            : tone === "accent" ? "text-accent"
            : tone === "muted" ? "text-muted-foreground" : "";
  return (
    <Card className="p-4 border-border bg-white">
      <div className="eyebrow text-muted-foreground">{label}</div>
      <div className={`font-display text-2xl font-bold mt-1 ${cls}`}>{value}</div>
    </Card>
  );
}

function EditTxnDialog({ row, onClose, onDone }) {
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (row) setF({
      amount: row.amount ?? "",
      mode: row.mode || "cash",
      payment_date: row.payment_date || "",
      reference: row.reference || "",
      notes: row.notes || "",
    });
  }, [row]);
  if (!row) return null;
  const submit = async (e) => {
    e.preventDefault();
    const amt = Number(f.amount);
    if (!Number.isFinite(amt) || amt < 0) return toast.error("Amount must be a non-negative number");
    setBusy(true);
    try {
      await api.put(`/payments/${row.id}`, {
        amount: amt,
        mode: f.mode,
        payment_date: f.payment_date || null,
        reference: f.reference || null,
        notes: f.notes || null,
      });
      toast.success("Transaction corrected. Dashboard + P&L will refresh.");
      onDone?.();
    } catch (err) { toast.error(formatApiError(err) || "Save failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="edit-txn-dialog">
        <DialogHeader>
          <DialogTitle>Correct Transaction</DialogTitle>
          <DialogDescription>
            {row.party_name || "—"} · Receipt {row.receipt_no || "N/A"} · Original ₹{Number(row.amount).toLocaleString("en-IN")}.
            Payment history is preserved; recorded corrections are stamped in the audit log.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Corrected Amount</Label>
              <Input type="number" step="1" min="0" value={f.amount}
                     onChange={(e) => setF({ ...f, amount: e.target.value })}
                     data-testid="edit-txn-amount" autoFocus />
            </div>
            <div className="space-y-1">
              <Label>Payment Mode</Label>
              <Select value={f.mode} onValueChange={(v) => setF({ ...f, mode: v })}>
                <SelectTrigger data-testid="edit-txn-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(MODE_LABELS).map(([v, l]) => (
                    <SelectItem key={v} value={v}>{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Payment Date</Label>
              <Input type="date" value={f.payment_date}
                     onChange={(e) => setF({ ...f, payment_date: e.target.value })}
                     data-testid="edit-txn-date" />
            </div>
            <div className="space-y-1">
              <Label>Reference</Label>
              <Input value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })}
                     placeholder="Txn ID / UTR / Cheque no." data-testid="edit-txn-ref" />
            </div>
          </div>
          <div className="space-y-1">
            <Label>Notes / Correction reason</Label>
            <Input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })}
                   placeholder="Why is this being corrected?" data-testid="edit-txn-notes" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={busy} data-testid="edit-txn-save">
              {busy ? "Saving…" : "Save Correction"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function VoidTxnDialog({ row, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (row) setReason(""); }, [row]);
  if (!row) return null;
  const confirm = async () => {
    if (!reason.trim()) return toast.error("Please give a reason for the void");
    setBusy(true);
    try {
      await api.delete(`/payments/${row.id}?reason=${encodeURIComponent(reason.trim())}`);
      toast.success("Transaction voided. Dashboard + P&L will refresh.");
      onDone?.();
    } catch (err) { toast.error(formatApiError(err) || "Void failed"); }
    finally { setBusy(false); }
  };
  return (
    <AlertDialog open onOpenChange={(v) => !v && onClose()}>
      <AlertDialogContent data-testid="void-txn-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Void this transaction?</AlertDialogTitle>
          <AlertDialogDescription>
            <div className="mb-2">
              <span className="font-medium">{row.party_name || "—"}</span> · {row.payment_date} ·
              {" "}<span className="font-mono">₹{Number(row.amount).toLocaleString("en-IN")}</span> ·
              {" "}{MODE_LABELS[row.mode] || row.mode}
            </div>
            Voided transactions stop counting in Dashboard, P&L and Daily / Monthly reports immediately.
            The original record is preserved for audit and can be viewed by filtering "Voided only".
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-1">
          <Label>Reason for void</Label>
          <Input value={reason} onChange={(e) => setReason(e.target.value)}
                 placeholder="e.g. Duplicate entry / wrong resident / entry mistake"
                 data-testid="void-txn-reason" autoFocus />
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={confirm} disabled={busy}
                             data-testid="void-txn-confirm"
                             className="bg-destructive text-destructive-foreground">
            {busy ? "Voiding…" : "Confirm Void"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
